/** How long an anchor may take to render before we give up waiting for it. */
const ANCHOR_WAIT_MS = 3000;

/** How long the flash class stays on, matching the CSS animation it drives. */
const FLASH_DURATION_MS = 2000;

/**
 * The highlight itself — add the class, take it off again once the animation has run.
 *
 * Shared by {@link scrollToAnchorWhenReady} and {@link flashAnchorWhenReady} so the two ways a row can be
 * marked cannot come to mark it differently. A second copy would be a second duration and a second class
 * lifecycle, and the first one to drift would leave a row highlighted permanently.
 */
function flashElement(el: Element, flashClass: string): void {
  el.classList.add(flashClass);
  setTimeout(() => el.classList.remove(flashClass), FLASH_DURATION_MS);
}

/**
 * Flash an anchored row **without scrolling to it**, once its anchor has rendered.
 *
 * Added for the scheduling queue's multi-id `?visitId=` deep link: a signed ATP can open several site
 * visits, and the mail links to all of them. Only the first is opened and scrolled to — there is one
 * viewport and one open row, so scrolling to the rest would fight itself — but the others still have to be
 * findable, so they are marked in place and the PA scrolls to them at their own pace.
 *
 * 🔴 This waits on `requestAnimationFrame` for the same reason {@link scrollToAnchorWhenReady} does, and
 * the reason is not the scroll: the caller runs inside the queue subscribe, BEFORE Angular has laid out the
 * rows it just built, so a straight `getElementById` finds nothing and silently marks nothing. It bails on
 * teardown or after the same time cap, so a row that never renders gives up quietly.
 *
 * It registers no scroll-cancelling gesture listeners — there is no scroll here for a gesture to interrupt,
 * and a queue deep-linking several rows would otherwise install and tear down a listener set per row.
 *
 * @param anchorId    the element id to mark
 * @param flashClass  the class applied for the flash's duration once the anchor exists
 * @param isDestroyed callback so the caller's ngOnDestroy can stop the loop
 */
export function flashAnchorWhenReady(
  anchorId: string,
  flashClass: string,
  isDestroyed: () => boolean
): void {
  const deadline = performance.now() + ANCHOR_WAIT_MS;

  const step = () => {
    if (isDestroyed() || performance.now() >= deadline) {
      return;
    }
    const el = document.getElementById(anchorId);
    if (!el) {
      requestAnimationFrame(step); // anchor not laid out yet — keep waiting
      return;
    }
    flashElement(el, flashClass);
  };

  requestAnimationFrame(step);
}

/**
 * Scroll the window to a project card (`#project-<id>`) and briefly flash it —
 * used when the vendor arrives from the Dashboard work-map's "View project".
 *
 * Two things make a naive one-shot `scrollIntoView` unreliable on an in-app
 * navigation (and are why this needs to be more than a single call):
 *
 *  1. The list renders asynchronously — the data subscribe fires, then Angular
 *     lays out the cards (each pulling a static-map image), so the `#project-*`
 *     anchor may not exist for a frame or two after we're asked to scroll.
 *
 *  2. The Router runs with `scrollPositionRestoration: 'enabled'` (app.config.ts)
 *     and the window is the app's only scroll container (vendor-shell), so on a
 *     FORWARD navigation the router scrolls the window to top shortly after
 *     NavigationEnd — which lands just after our first scroll and stomps it back
 *     to the top. That's why the bug only showed on navigation: a hard refresh
 *     has no forward-nav scroll-to-top racing us, and its data arrives over the
 *     wire (slower), so the single scroll used to win.
 *
 * So instead of scrolling once, we re-assert the target position each frame
 * until it has held still for two consecutive frames — i.e. the router's
 * one-shot scroll-to-top has already fired and we've overridden it. We use an
 * instant (not smooth) alignment because a smooth animation would fight the
 * re-assert. We bail on teardown, on a genuine user scroll gesture, or after a
 * time cap so a filtered-out / never-rendered target gives up quietly.
 *
 * @param projectId   the project whose `#project-<id>` anchor to reveal
 * @param isDestroyed callback so the caller's ngOnDestroy can stop the loop
 */
export function scrollToProjectWhenReady(
  projectId: string,
  isDestroyed: () => boolean
): void {
  scrollToAnchorWhenReady(
    'project-' + projectId,
    'project-group--flash',
    isDestroyed
  );
}

/**
 * The same scroll-and-flash for any anchored row, by element id and flash class.
 *
 * Added for the scheduling queue's `?visitId=` deep link (the PA site-visit notification email lands
 * there with one visit to reveal). Everything in the comment above still applies verbatim — an
 * asynchronously-rendered anchor, and the router's forward-navigation scroll-to-top racing us — which is
 * exactly why this is the same implementation and not a second one: a re-implementation would have to
 * re-derive both of those, and the first one someone gets wrong fails only on navigation, never on refresh.
 * {@link scrollToProjectWhenReady} is now a thin call into this with the project anchor's own naming.
 *
 * The highlight at the end is {@link flashElement}, shared with {@link flashAnchorWhenReady} — the
 * flash-only path for the rows a multi-id deep link marks but does not open.
 *
 * @param anchorId    the element id to reveal
 * @param flashClass  the class applied for the flash's duration once the scroll has held still
 * @param isDestroyed callback so the caller's ngOnDestroy can stop the loop
 */
export function scrollToAnchorWhenReady(
  anchorId: string,
  flashClass: string,
  isDestroyed: () => boolean
): void {
  const deadline = performance.now() + ANCHOR_WAIT_MS;
  let stableFrames = 0;
  let lastSetY = Number.NaN;
  let cancelled = false;

  // Only a genuine user gesture cancels the auto-scroll. We deliberately do NOT
  // listen to the 'scroll' event — our own programmatic scrolls would trip it.
  const onUserGesture = () => {
    cancelled = true;
  };
  const passive: AddEventListenerOptions = { passive: true };
  window.addEventListener('wheel', onUserGesture, passive);
  window.addEventListener('touchmove', onUserGesture, passive);
  window.addEventListener('keydown', onUserGesture);

  const cleanup = () => {
    window.removeEventListener('wheel', onUserGesture, passive);
    window.removeEventListener('touchmove', onUserGesture, passive);
    window.removeEventListener('keydown', onUserGesture);
  };

  const step = () => {
    if (cancelled || isDestroyed() || performance.now() >= deadline) {
      cleanup();
      return;
    }

    const el = document.getElementById(anchorId);
    if (!el) {
      requestAnimationFrame(step); // anchor not laid out yet — keep waiting
      return;
    }

    // Has the position we set last frame stuck (nothing yanked it away)?
    if (!Number.isNaN(lastSetY) && Math.abs(window.scrollY - lastSetY) <= 2) {
      stableFrames++;
      if (stableFrames >= 2) {
        flashElement(el, flashClass);
        cleanup();
        return;
      }
    } else {
      // First pass, or the router (or layout shift) moved us — (re)assert.
      el.scrollIntoView({ block: 'start' });
      lastSetY = window.scrollY;
      stableFrames = 0;
    }
    requestAnimationFrame(step);
  };

  requestAnimationFrame(step);
}
